import { browserHistory } from 'react-router';
import {
  all,
  call,
  cancel,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import uuid from 'uuid/v4';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import * as api from '../../services/dashboard/api_dashboard';
import * as apiCsvExport from '../../services/csv_export/api_csv_export';
import * as apiEmployee from '../../services/employee/api_employee';
import * as apiReport from '../../services/report/apiReport';

import * as SagasType from '../../constants/constant_saga_types';

import {
  dashboardCreateSelector,
  dashboardDataSelector,
  dashboardEditModeSelector,
  dashboardLayoutSelector,
  dashboardListDataSelector,
  dashboardPortletSelector,
  dashboardPortletsSelector,
  getDashboard,
  portletMetaSelector,
} from '../../components/admin/dashboard/selectors';

import { parseSearchRecordCompareFilters, parseSearchRecordFilters } from '../record/saga_record';

export default function* (app) {
  return yield all([
    fork(addPortletSaga, app),
    fork(changeDashboardAutoRefreshSaga, app),
    fork(changeLayoutDashboardSaga),
    fork(changePortletAutoRefreshSaga, app),
    fork(changePortletsCriteriaSaga),
    fork(changePortletViewSaga),
    fork(createDashboardSaga),
    fork(deleteDashboardSaga, app),
    fork(deletePortletSaga),
    fork(drilldownToReportSaga, app),
    fork(executeAllPortletsSaga),
    fork(executePortletSaga, app),
    fork(fetchDashboardListSaga, app),
    fork(fetchDashboardSaga, app),
    fork(movePortletSaga),
    fork(resetEditDashboardSaga),
    fork(resyncPorltetSaga, app),
    fork(saveDashboardSaga, app),
    fork(setDashboardSaga, app),
  ]);
}

function getError(err) {
  const type = get(err, 'cause.name');
  let message = '';

  switch (type) {
    case 'AlreadyExistsError':
      message = 'Dashboard already exists';
      break;

    default:
      message =
        get(err, 'cause.message') ||
        err.message ||
        'There was an error with you submission';
      break;
  }

  return message;
}

function getNewCriteria(prevCriteria, currCriteria) {
  const {
    filters: prevFilters,
    sorting: prevSorting,
    compare_filters: prevCompareFilters,
  } = prevCriteria;

  const {
    filters: currFilters,
    sorting: currSorting,
    compare_filters: currCompareFilters,
  } = currCriteria;

  const setNewFieldValue = (prevFields, valueField) => {
    return (currField) => {
      const { id: currFieldId } = currField;
      const prevField = prevFields.find(
        ({ id: prevFieldId }) => currFieldId === prevFieldId
      );

      currField[valueField] = get(prevField, valueField, null);

      return currField;
    };
  };

  const filters = (prevFilters || []).length
    ? (currFilters || []).map(setNewFieldValue(prevFilters, 'value'))
    : currFilters;

  const sorting = (prevSorting || []).length
    ? (currSorting || []).map(setNewFieldValue(prevSorting, 'value'))
    : currSorting;

  const compare_filters = isEmpty(prevCompareFilters)
    ? currCompareFilters
    : prevCompareFilters;

  const criteria = { filters, sorting, compare_filters };

  return criteria;
}

function* fetchDashboardListSaga(app) {
  yield takeLatest(SagasType.FETCH_DASHBOARD_LIST, fetchDashboardList, app);
}

function* fetchDashboardList(app, action) {
  const { payload } = action;
  const { searchTerm } = payload || {};

  try {
    const data = yield call(api.fetchDashboardDropdown, app, {
      searchTerm,
    });

    yield put({
      type: SagasType.FETCH_DASHBOARD_LIST_DONE,
      payload: data,
    });
  } catch (err) {
    yield put({
      type: SagasType.FETCH_DASHBOARD_LIST_ERROR,
      payload: getError(err),
    });
  }
}

function* drilldownToReportSaga(app) {
  yield takeEvery(SagasType.DRILLDOWN_TO_REPORT, drilldownToReport, app);
}

function* drilldownToReport(app, action) {
  const {
    key,
    item,
    location,
    subsidiary,
    customer,
    classification,
    reportId,
    reportType,
    portletType,
    tableRows,
  } = action;

  // Look for the dashboard filter
  const state = yield select();
  const loadedReports = state.report.byResultDash || {};
  const obj = loadedReports[key] || {};

  // Fill up the filters with whatever was submitted
  const { filters } = obj;
  if (filters) {
    if (item && !isEqual(item)) {
      filters.item = [...item];
    }
    if (location && !isEqual(location)) {
      filters.location = [...location];
    }
    if (subsidiary && !isEqual(subsidiary)) {
      filters.subsidiary = [...subsidiary];
    }
    if (customer && !isEqual(customer)) {
      filters.customer = [...customer];
    }
    if (classification && !isEqual(classification)) {
      filters.classification = [...classification];
    }
  }

  yield fork(triggerReport, reportId, reportType, portletType, tableRows, obj);

  // Redirect to the report
  browserHistory.push(`/admin/report/${reportId}`);
}

function* triggerReport(reportId, reportType, portletType, tableRows, obj) {
  // Run the report with these filters
  yield put({
    type: SagasType.EXECUTE_REPORT,
    key: reportId,
    reportId,
    reportType,
    portletType,
    tableRows,
    reportFilters: obj.filters,
  });
}

function* fetchDashboardSaga(app) {
  yield takeEvery(SagasType.FETCH_DASHBOARD, fetchDashboard, app);
}

function* fetchDashboard(app, action) {
  try {
    const { user } = yield select();
    const { dashboard_id } = user;

    const { payload } = action;
    const { id: payloadId } = payload || {};

    yield put({ type: SagasType.FETCH_DASHBOARD_INPROGRESS });

    const id = payloadId || dashboard_id;

    const dashboard = yield call(api.fetchDashboardById, app, {
      id,
    });

    dashboard.portlets = dashboard.portlets.map((portlet) => {
      const { criteria, search_record } = portlet;

      portlet.criteria = search_record
        ? getNewCriteria(criteria, search_record)
        : criteria;

      return portlet;
    });

    yield put({
      type: SagasType.FETCH_DASHBOARD_DONE,
      payload: dashboard || null,
    });
  } catch (error) {
    yield put({
      type: SagasType.FETCH_DASHBOARD_ERROR,
      payload: getError(error),
    });
  }
}

function* saveDashboardSaga(app) {
  yield takeLatest(SagasType.SAVE_DASHBOARD, saveDashboard, app);
}

function* saveDashboard(app, action) {
  const { payload } = action;
  const { isSilent } = payload || {};

  if (!isSilent) {
    yield put({ type: SagasType.SAVE_DASHBOARD_INPROGRESS });
  }

  const state = yield select();
  const data = dashboardDataSelector(state);

  try {
    const response = yield call(api.saveDashboard, app, { data });

    if (!isSilent) {
      data.id = response.id;
      data.created_by_id = response.created_by_id;

      const isCreate = dashboardCreateSelector(state);
      const isEditMode = dashboardEditModeSelector(state);
      const edit = isEditMode ? cloneDeep(data) : null;

      if (isCreate) {
        yield put({
          type: SagasType.SET_DASHBOARD,
          payload: { id: response.id, isHome: true, isSilent: true },
        });
      }

      yield put({
        type: SagasType.SAVE_DASHBOARD_DONE,
        payload: { edit, data },
      });
    }
  } catch (err) {
    yield put({ type: SagasType.SAVE_DASHBOARD_ERROR, payload: getError(err) });
  }
}

function* executeAllPortletsSaga() {
  yield takeLatest(SagasType.EXECUTE_ALL_DASHBOARD_PORTLET, executeAllPortlets);
}

function* executeAllPortlets(action) {
  const portlets = yield select(dashboardPortletsSelector);
  const { payload } = action;
  const { criteria, limit, offset, isSrExcluded } = payload || {};

  for (const portlet of portlets) {
    const { criteria: currCriteria, id, search_record: searchRecord } = portlet;

    if (isSrExcluded && searchRecord) continue;

    yield put({
      type: SagasType.EXECUTE_DASHBOARD_PORTLET,
      payload: {
        id,
        limit,
        offset,
        criteria: criteria || currCriteria,
      },
    });
  }
}

function* executePortletSaga(app) {
  yield takeEvery(SagasType.EXECUTE_DASHBOARD_PORTLET, executePortlet, app);
}

function* executePortlet(app, action) {
  const { payload } = action;
  const { id, criteria } = payload;

  try {
    yield put({
      type: SagasType.FETCH_DASHBOARD_PORTLET_INPROGRESS,
      payload: { id },
    });

    const state = yield select();

    const { portlet_report: portletReport, search_record: searchRecord } =
      dashboardPortletSelector(state, id);

    let service;
    let record;

    const params = {};

    if (portletReport) {
      service = executeReportPortlet;
      record = portletReport;
    }

    if (searchRecord) {
      const { limit: payloadLimit, offset: payloadOffset } = payload;
      const { limit: metaLimit, offset: metaOffset } = portletMetaSelector(
        state,
        id
      );
      const limit = payloadLimit === undefined ? metaLimit : payloadLimit;
      const offset = payloadOffset === undefined ? metaOffset : payloadOffset;

      params.limit = limit;
      params.offset = offset;
      service = executeSearchRecord;
      record = searchRecord;

      const { compare_filters, filters } = criteria;

      if (compare_filters || filters) {
        criteria.compare_filters = yield call(parseSearchRecordCompareFilters, compare_filters);

        yield fork(changePortletCriteria, {
          payload: {
            id,
            criteria,
          }
        });
      }
    }

    params.criteria = criteria;

    const data = yield call(service, app, record, params);

    yield put({
      type: SagasType.FETCH_DASHBOARD_PORTLET_DONE,
      payload: { id, data },
    });
  } catch (err) {
    yield put({
      type: SagasType.FETCH_DASHBOARD_PORTLET_ERROR,
      payload: {
        id,
        error: getError(err),
      }
    });
  }
}

function* executeSearchRecord(app, searchRecord, params) {
  const { criteria, limit, offset } = params;
  const { filters, sorting, compare_filters } = criteria;
  const { record_type, fields, query, having_query } = searchRecord;
  const parsedFilters = yield call(parseSearchRecordFilters, filters || []);

  const data = yield call(apiCsvExport.previewSearchRecord, app, {
    compare_filters,
    fields,
    having_query,
    limit,
    offset,
    query,
    record_type,
    sorting,
    filters: parsedFilters,
  });

  return data;
}

async function executeReportPortlet(app, portletReport, query) {
  const { criteria: reportFilters } = query;
  const { report_id: reportId, cid: portletType } = portletReport;

  const params = {
    reportId,
    portletType,
    reportFilters,
  };

  const results = await apiReport.executeReport(app, params);
  return results;
}

function* movePortletSaga() {
  yield takeEvery(SagasType.MOVE_PORTLET, movePortlet);
}

function movePortletUtil(params) {
  const { portlets, source, destination } = params;
  const { column: srcCol, position: srcPos } = source;
  const { column: dstCol, position: dstPos } = destination;

  const newPortlets = portlets.map((portlet) => {
    const { column, position } = portlet;

    if (column === srcCol && position === srcPos) {
      portlet.column = dstCol;
      portlet.position = dstPos;
      return portlet;
    }

    if (column === srcCol && column === dstCol) {
      const moveFxr = Math.sign(srcPos - dstPos);
      const newPos = position + moveFxr;

      if (moveFxr === -1 && position >= srcPos && position <= dstPos) {
        portlet.position = newPos;
        return portlet;
      }

      if (moveFxr === 1 && position <= srcPos && position >= dstPos) {
        portlet.position = newPos;
        return portlet;
      }

      return portlet;
    }

    if (column === srcCol) {
      portlet.position = srcPos <= position ? position - 1 : position;
      return portlet;
    }

    if (column === dstCol) {
      portlet.position = dstPos <= position ? position + 1 : position;
      return portlet;
    }

    return portlet;
  });

  return newPortlets;
}

function* movePortlet(action) {
  const { payload } = action;
  const { source, destination, isSave } = payload;
  const portlets = yield select(dashboardPortletsSelector);
  const newPortlets = movePortletUtil({ portlets, source, destination });

  if (isSave) {
    yield put({
      type: SagasType.SAVE_DASHBOARD,
      payload: { isSilent: true },
    });
  }

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { portlets: newPortlets },
  });
}

function* deletePortletSaga() {
  yield takeEvery(SagasType.DELETE_PORTLET, deletePortlet);
}

function* deletePortlet(action) {
  const { payload } = action;
  const { id, isSave } = payload;
  const portlets = yield select(dashboardPortletsSelector);

  let portletId = id;

  const portletIndex = portlets.findIndex(
    ({ id: currId }) => currId === portletId
  );

  const { column, position } = portlets[portletIndex];

  portlets.splice(portletIndex, 1);

  const newPortlets = movePortletUtil({
    portlets: [...portlets],
    source: { column, position },
    destination: { column: -1, position: -1 },
  });

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { portlets: newPortlets },
  });

  yield put({
    type: SagasType.DELETE_PORTLET_META,
    payload: portletId,
  });

  if (isSave) {
    yield put({
      type: SagasType.SAVE_DASHBOARD,
      payload: { isSilent: true },
    });
  }
}

function* changePortletsCriteriaSaga() {
  const taskCollection = {};

  while (true) {
    const action = yield take(SagasType.CHANGE_PORTLET_CRITERIA);
    const { payload } = action;
    const { id } = payload;

    if (Boolean(taskCollection[id])) {
      yield cancel(taskCollection[id]);
    }

    taskCollection[id] = yield fork(changePortletCriteria, action);
  }
}

function* changePortletCriteria(action) {
  const { payload } = action;
  const { id, criteria, isExecute, isApplyAll, isSilent } = payload;

  const portlets = yield select(dashboardPortletsSelector);
  const newPortlets = portlets.map((portlet) => {
    if (
      (id !== portlet.id && !isApplyAll) ||
      (isApplyAll && portlet.search_record)
    ) {
      return portlet;
    }

    portlet.criteria = criteria;

    return portlet;
  });

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { portlets: newPortlets },
  });

  if (isExecute) {
    yield put({
      payload: {
        criteria,
        id,
        isSilent,
        isSrExcluded: true,
        limit: 10,
        offset: 0,
      },
      type: isApplyAll
        ? SagasType.EXECUTE_ALL_DASHBOARD_PORTLET
        : SagasType.EXECUTE_DASHBOARD_PORTLET,
    });
  }
}

function* changePortletViewSaga() {
  yield takeEvery(SagasType.CHANGE_PORTLET_VIEW, changePortletView);
}

function* changePortletView(action) {
  const { payload } = action;
  const { id, view } = payload;

  const portlets = yield select(dashboardPortletsSelector);
  const newPortlets = portlets.map((portlet) => {
    if (id !== portlet.id) return portlet;

    portlet.view_type = view;

    return portlet;
  });

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { portlets: newPortlets },
  });
}

function* changePortletAutoRefreshSaga() {
  yield takeEvery(SagasType.CHANGE_PORTLET_AUTO_REFRESH, changePortletAutoRefresh);
}

function* changePortletAutoRefresh(action) {
  const { payload } = action;
  const {
    auto_refresh_interval_ms,
    id,
    is_auto_refresh,
    isSave,
  } = payload;

  const portlets = yield select(dashboardPortletsSelector);
  const newPortlets = portlets.map((portlet) => {
    if (id !== portlet.id) return portlet;

    portlet.is_auto_refresh = is_auto_refresh;
    portlet.auto_refresh_interval_ms = auto_refresh_interval_ms;

    return portlet;
  });

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { portlets: newPortlets },
  });

  if (isSave) {
    yield put({
      type: SagasType.SAVE_DASHBOARD,
      payload: { isSilent: true },
    });
  }
}

function* resetEditDashboardSaga() {
  yield takeEvery(SagasType.RESET_EDIT_DASHBOARD, resetEditDashboard);
}

function* resetEditDashboard(action) {
  const { data, edit } = yield select(getDashboard);
  const { payload } = action;
  const { isCancelEditMode } = payload || {};

  const portletsData = get(data, 'portlets', []);
  const portletsEdit = get(edit, 'portlets', []);

  for (const portletData of portletsData) {
    const { id: portletDataId, criteria: portletDataCriteria } = portletData;

    const portletEdit =
      portletsEdit.find(
        ({ id: portletEditId }) => portletEditId === portletDataId
      ) || {};

    const { criteria: portletEditCriteria } = portletEdit;

    if (isEqual(portletDataCriteria, portletEditCriteria)) continue;

    yield put({
      type: SagasType.EXECUTE_DASHBOARD_PORTLET,
      payload: {
        id: portletDataId,
        criteria: portletDataCriteria,
      },
    });
  }

  if (isCancelEditMode) {
    yield put({ type: SagasType.EDIT_DASHBOARD, payload: false });
  } else {
    yield put({ type: SagasType.RESET_EDIT_DASHBOARD_DONE });
  }
}

function* addPortletSaga(app) {
  yield takeEvery(SagasType.ADD_PORTLET, addPortlet, app);
}

function* addPortlet(app, action) {
  const portlets = yield select(dashboardPortletsSelector);
  const { payload } = action;
  const { column, isSave, portlet_report_id, position, search_record_id } =
    payload;

  const id = uuid();

  const portlet = {
    ...payload,
    id,
    column: -1,
    position: -1,
    isNew: true,
    search_record_id,
    portlet_report_id,
  };

  const newPortlets = movePortletUtil({
    portlets: [...portlets, portlet],
    source: { column: -1, position: -1 },
    destination: { column, position },
  });

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { portlets: newPortlets },
  });

  yield put({
    type: SagasType.RESYNC_PORTLET,
    payload: { id, isSave },
  });
}

function* changeLayoutDashboardSaga() {
  yield takeEvery(SagasType.CHANGE_DASHBOARD_LAYOUT, changeLayoutDashboard);
}

function* changeLayoutDashboard(action) {
  const state = yield select();
  const { payload } = action;
  const { layout: currLayout, isSave } = payload || {};
  const prevLayout = dashboardLayoutSelector(state);
  const currLayoutLength = currLayout.length;
  const prevLayoutLength = prevLayout.length;

  if (!currLayoutLength) return;

  const portlets = dashboardPortletsSelector(state);

  if (currLayoutLength < prevLayoutLength) {
    const cursors = getCursors(portlets);
    let newCol = 0;

    for (const portlet of portlets) {
      const { column } = portlet;

      if (column <= currLayoutLength - 1) continue;

      const colMaxPos = cursors[newCol];
      const newPos = colMaxPos ? colMaxPos + 1 : 0;

      portlet.column = newCol;
      portlet.position = newPos;

      cursors[newCol] = newPos;
      newCol = newCol === currLayoutLength - 1 ? 0 : newCol + 1;
    }
  }

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: { layout: currLayout, portlets },
  });

  if (isSave) {
    yield put({
      type: SagasType.SAVE_DASHBOARD,
      payload: { isSilent: true },
    });
  }
}

function* changeDashboardAutoRefreshSaga() {
  yield takeEvery(SagasType.CHANGE_DASHBOARD_AUTO_REFRESH, changeDashboardAutoRefresh);
}

function* changeDashboardAutoRefresh(action) {
  const { payload } = action;
  const {
    auto_refresh_interval_ms,
    is_auto_refresh,
    isSave,
  } = payload;

  yield put({
    type: SagasType.CHANGE_DASHBOARD_DATA,
    payload: {
      auto_refresh_interval_ms,
      is_auto_refresh,
    },
  });

  if (isSave) {
    yield put({
      type: SagasType.SAVE_DASHBOARD,
      payload: { isSilent: true },
    });
  }
}

function getCursors(portlets) {
  const cursors = [];

  for (const portlet of portlets) {
    const { column, position } = portlet;
    const prevCursor = cursors[column] || 0;
    cursors[column] = prevCursor < position ? position : prevCursor;
  }

  return cursors;
}

function* createDashboardSaga() {
  yield takeLatest(SagasType.CREATE_DASHBOARD, createDashboard);
}

function* createDashboard(action) {
  const { payload: isSaveAs } = action;
  const dashboardPlaceholder = {
    is_private: false,
    layout: [4, 4, 4, 4],
    name: 'New dashboard',
    portlets: [],
  };

  let editDashboard = dashboardPlaceholder;

  if (isSaveAs) {
    const state = yield select();
    const dashboard = dashboardDataSelector(state);

    editDashboard = cloneDeep(dashboard);
    editDashboard._id = undefined;
    editDashboard.cid = undefined;
    editDashboard.context = undefined;
    editDashboard.created_by_id = undefined;
    editDashboard.created_datetime = undefined;
    editDashboard.createdAt = undefined;
    editDashboard.i18n = undefined;
    editDashboard.id = undefined;
    editDashboard.last_updated = undefined;
    editDashboard.last_updated_by_id = undefined;
    editDashboard.name = `${editDashboard.name} (COPY)`;
    editDashboard.reference_id = undefined;
    editDashboard.system_defined = undefined;
    editDashboard.updatedAt = undefined;

    const { portlets } = editDashboard;

    editDashboard.portlets = portlets.map((portlet) => ({
      ...portlet,
      isNew: true,
      createdAt: undefined,
      updatedAt: undefined,
      created_by_id: undefined,
      last_updated_by_id: undefined,
      created_datetime: undefined,
      last_updated: undefined,
    }));

    editDashboard.name = `${editDashboard.name}`;
  }

  let createDashboard = { ...editDashboard, name: '' };

  yield put({
    type: SagasType.CREATE_DASHBOARD_DONE,
    payload: {
      create: createDashboard,
      edit: editDashboard,
    },
  });
}

function* deleteDashboardSaga(app) {
  yield takeLatest(SagasType.DELETE_DASHBOARD, deleteDashboard, app);
}

function* deleteDashboard(app, action) {
  const state = yield select();
  const { payload } = action;
  const { id } = payload || {};

  try {
    yield call(api.deleteDashboard, app, id);

    const dashboards = dashboardListDataSelector(state);

    yield put({
      type: SagasType.DELETE_DASHBOARD_DONE,
      payload: dashboards.filter(({ id: dashboardId }) => dashboardId !== id),
    });
  } catch (err) {
    yield put({
      type: SagasType.DELETE_DASHBOARD_ERROR,
      payload: getError(err),
    });
  }
}

function* resyncPorltetSaga(app) {
  yield takeLatest(SagasType.RESYNC_PORTLET, resyncPortlet, app);
}

function* resyncPortlet(app, action) {
  const state = yield select();
  const { payload } = action;
  const { isSave, id } = payload;

  yield put({
    type: SagasType.SETUP_PORTLET,
    payload: { id },
  });

  const portlets = dashboardPortletsSelector(state);
  const portlet = portlets.find(({ id: portletId }) => portletId === id);

  const { search_record_id, portlet_report_id, criteria } = portlet;

  try {
    if (search_record_id) {
      const searchRecord = yield call(apiCsvExport.fetchCSVExportById, app, {
        id: search_record_id,
      });

      portlet.search_record = searchRecord;
      portlet.criteria = getNewCriteria(criteria, searchRecord);
    }

    if (portlet_report_id) {
      const portletReport = yield call(
        api.fetchPortletReportById,
        app,
        portlet_report_id
      );

      portlet.portlet_report = portletReport;
    }

    yield put({
      type: SagasType.CHANGE_DASHBOARD_DATA,
      payload: { portlets },
    });

    yield put({
      type: SagasType.SETUP_PORTLET_DONE,
      payload: { id },
    });

    yield put({
      type: SagasType.EXECUTE_DASHBOARD_PORTLET,
      payload: { id, criteria: portlet.criteria, limit: 10, offset: 0 },
    });

    if (isSave) {
      yield put({
        type: SagasType.SAVE_DASHBOARD,
        payload: { isSilent: true },
      });
    }
  } catch (err) {
    yield put({
      type: SagasType.SETUP_PORTLET_ERROR,
      payload: { id, error: getError(err) },
    });
  }
}

function* setDashboardSaga(app) {
  yield takeLatest(SagasType.SET_DASHBOARD, setDashboard, app);
}

function* setDashboard(app, action) {
  const { user, tenant } = yield select();
  const { payload } = action;
  const { id, isHome, isSilent } = payload;

  try {
    yield call(apiEmployee.saveEmployee, app, {
      tenant,
      user,
      data: { ...user, dashboard_id: id },
    });

    if (!isSilent) {
      yield put({
        type: SagasType.FETCH_DASHBOARD,
        payload: { id },
      });
    }

    yield put({ type: SagasType.SET_DASHBOARD_DONE });
  } catch (err) {
    yield put({
      type: SagasType.SET_DASHBOARD_ERROR,
      payload: {
        isHome,
        error: getError(err),
      },
    });
  }
}
